import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiRequest from 'api/apiRequest';
import endpoints from 'api/endpoints';
import getProductId from 'store/product/selector/getProductId';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import { useTranslation } from 'react-i18next';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import getDeliveryOptions from 'store/product/selector/getDeliveryOptions';
import getProductQuantity from 'store/product/selector/getProductQuantity';
import OPPlusFreeShippingV2 from '../OPPlusFreeShippingV2/OPPlusFreeShippingV2';
import styles from './DeliveryAndOPPlusFreeShipping.module.scss';

interface ShipmentMethodResponse {
  isEnabled: boolean;
  methods: {
    minDeliveryDays: string;
    maxDeliveryDays: string;
  }[];
}
const rangeOrOneNumber = (a: string, b: string) => (a === b ? a : `${a}-${b}`);

const DeliveryAndOPPlusFreeShipping = () => {
  const [deliveryInDays, setDeliveryInDays] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const productId = useSelector(getProductId);
  const { t } = useTranslation();
  const deliveryOptions = useSelector(getDeliveryOptions);
  const quantity = useSelector(getProductQuantity) || 1;

  const setDeliveryInDaysFromDeliveryOptions = () => {
    if (!deliveryOptions || !deliveryOptions.delivery || !deliveryOptions.delivery.length) {
      return;
    }
    const deliveryMethodsSorted = deliveryOptions.delivery
      .map((d) => d)
      .sort((a, b) => Number(b.minDeliveryDay) - Number(a.minDeliveryDay));

    setDeliveryInDays(
      rangeOrOneNumber(deliveryMethodsSorted[0].minDeliveryDay, deliveryMethodsSorted[0].maxDeliveryDay),
    );
  };

  const fetchShipmentDetails = async () => {
    if (!productId) return;
    try {
      const shipmentDetailsResponse = await apiRequest<ShipmentMethodResponse>(
        endpoints.shipmentMethodV1List,
        { productId, quantity },
        'GET',
        'data',
      );
      if (shipmentDetailsResponse.isEnabled && shipmentDetailsResponse.methods.length) {
        const sortedMethods = shipmentDetailsResponse.methods
          .map((d) => d)
          .sort((a, b) => Number(b.minDeliveryDays) - Number(a.minDeliveryDays));
        const deliveryMethod = sortedMethods[0];
        const days = rangeOrOneNumber(deliveryMethod.minDeliveryDays, deliveryMethod.maxDeliveryDays);
        setDeliveryInDays(days);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching shipment details', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    // if delivery options defined, and delivery days was not
    // set yet set it from delivery options
    if (deliveryOptions && !deliveryInDays) {
      setDeliveryInDaysFromDeliveryOptions();
    }
  }, [deliveryOptions, deliveryInDays]);

  useEffect(() => {
    fetchShipmentDetails();
  }, [productId]);

  const deliveryDetailsReady = !isLoading && deliveryInDays;

  return (
    <div className={styles.section}>
      {deliveryDetailsReady && (
        <div className={styles.deliveryDetails}>
          <OpIcon icon="express-truck" size="medium" />
          <OpText variant="body-large-regular">{t('txt_delivery_in_days', { days: deliveryInDays })}</OpText>
        </div>
      )}
      <OPPlusFreeShippingV2 />
    </div>
  );
};

export default DeliveryAndOPPlusFreeShipping;
